import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { casesApi } from "./api";
import { dateLong, money } from "./format";

// The on-time promise on a case (builder P, API docs/on-time-promise.md):
// "Appointment to be booked by <date>" or "Visa due by <date>", red once
// missed (the appointment step not ticked in time, the visa not sent), with
// the task "Promise missed: refund $X": "Refund made" (who and when are
// kept) or "Not due" with the reason. Nothing before the case is paid, or
// when its visa promises nothing.

export const NOT_DUE = [
  { value: "customer_late", label: "The customer was late sending what we asked (more than 2 working days)" },
  { value: "missed_appointment", label: "The customer missed the appointment" },
];

/** What the card says, from the API's promise. */
export function promiseWords(promise, today = new Date().toISOString().slice(0, 10)) {
  if (!promise?.due_on) return null;
  const due = dateLong(promise.due_on);
  const title = promise.kind === "evisa_date" ? `Visa due by ${due}` : `Appointment to be booked by ${due}`;
  const fee = promise.fee > 0 ? money(promise.fee) : "Safrati’s fee";
  if (promise.state === "kept") {
    return {
      title,
      tone: "success",
      chip: "Kept",
      line: promise.met_on ? `${promise.kind === "evisa_date" ? "Visa sent or decided" : "Booked"} on ${dateLong(promise.met_on)}.` : null,
    };
  }
  if (promise.state === "closed") return { title, tone: "default", chip: "Closed", line: "The application was cancelled." };
  if (promise.state === "missed") {
    return { title, tone: "error", chip: "Missed", line: null, task: `Promise missed: refund ${fee}` };
  }
  const days = Math.round((Date.parse(`${promise.due_on}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  const left = days <= 0 ? "Due today" : days === 1 ? "1 day left" : `${days} days left`;
  const what = promise.kind === "evisa_date" ? "Send the visa by then" : "Book it and tick the appointment step by then";
  return { title, tone: days <= 1 ? "warning" : "info", chip: left, line: `${what}, or Safrati’s fee (${fee}) goes back to the customer.` };
}

export default function PromiseCard({ caseData, canEdit, onChanged }) {
  const dispatch = useDispatch();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const promise = caseData.promise;
  const words = promiseWords(promise);
  if (!words) return null;
  const resolution = promise.resolution;

  const decide = async (outcome, why = null) => {
    try {
      await casesApi.promise(caseData.id, outcome, why, dispatch);
      toast.success(
        outcome === "refunded"
          ? `Refund of ${words.task.replace("Promise missed: refund ", "")} recorded for case #${caseData.id}.`
          : `Recorded as not due for case #${caseData.id}.`
      );
      setAsking(false);
      onChanged();
      return true;
    } catch (err) {
      toast.error(err?.message || "Couldn’t save it. Nothing changed. Try again.");
      return false;
    }
  };

  return (
    <Card variant="outlined" id="case-promise" sx={words.tone === "error" && !resolution ? { borderColor: "error.main" } : undefined}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box minWidth={0}>
            <Typography variant="overline" color="text.secondary" component="p">
              On-time promise
            </Typography>
            <Typography variant="h6" component="h2" sx={{ color: words.tone === "error" && !resolution ? "error.main" : undefined }}>
              {words.title}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={words.tone === "default" ? "default" : words.tone}
            variant={words.tone === "error" ? "filled" : "outlined"}
            label={words.chip}
          />
        </Stack>
        {words.line && (
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {words.line}
          </Typography>
        )}
        {words.task && !resolution && (
          <Alert severity="error" sx={{ mt: 1.5 }} icon={false}>
            <Typography variant="body2" fontWeight={700}>
              {words.task}
            </Typography>
            <Typography variant="body2" mt={0.25}>
              Refund it the way the customer paid, then press Refund made. If the customer was late sending what we asked, or missed the appointment, it isn’t
              due.
            </Typography>
            {canEdit && (
              <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                <ActionButton variant="contained" size="small" busyLabel="Saving…" onClick={() => decide("refunded")}>
                  Refund made
                </ActionButton>
                <ActionButton variant="outlined" size="small" onClick={() => setAsking(true)}>
                  Not due…
                </ActionButton>
              </Stack>
            )}
          </Alert>
        )}
        {resolution && (
          <Typography variant="body2" mt={1} fontWeight={600}>
            {resolution.outcome === "refunded"
              ? `Refund made${resolution.amount ? ` (${money(resolution.amount)})` : ""}`
              : `Not due: ${resolution.reason_words || "a reason was recorded"}`}
            <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>
              {` · ${[resolution.by, resolution.at ? dateLong(resolution.at) : null].filter(Boolean).join(", ")}`}
            </Box>
          </Typography>
        )}
      </CardContent>
      {asking && (
        <Dialog open onClose={() => setAsking(false)} maxWidth="xs" fullWidth aria-labelledby="promise-not-due-title">
          <DialogTitle id="promise-not-due-title">Why isn’t the refund due?</DialogTitle>
          <DialogContent>
            <Typography id="promise-not-due-why" variant="body2" mb={1}>
              The promise holds when the customer sends what we ask within 2 working days and goes to the appointment.
            </Typography>
            <RadioGroup aria-labelledby="promise-not-due-why" value={reason} onChange={(e) => setReason(e.target.value)}>
              {NOT_DUE.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio />}
                  label={r.label}
                  sx={{ alignItems: "flex-start", "& .MuiFormControlLabel-label": { pt: 1 } }}
                />
              ))}
            </RadioGroup>
            {tried && !reason && (
              <Typography variant="body2" color="error" mt={1} role="alert">
                Choose a reason.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <ActionButton variant="text" size="medium" onClick={() => setAsking(false)}>
              Keep it open
            </ActionButton>
            <ActionButton
              variant="contained"
              size="medium"
              busyLabel="Saving…"
              onClick={() => {
                if (!reason) {
                  setTried(true);
                  return undefined;
                }
                return decide("not_due", reason);
              }}
            >
              Record as not due
            </ActionButton>
          </DialogActions>
        </Dialog>
      )}
    </Card>
  );
}

PromiseCard.propTypes = { caseData: PropTypes.object.isRequired, canEdit: PropTypes.bool, onChanged: PropTypes.func.isRequired };
